import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  X, Search, Sparkles, Clock, Image as ImageIcon, Layout, FileText,
  Briefcase, Calendar, GraduationCap, Sliders, ArrowRight,
} from 'lucide-react';
import {
  CATEGORIES, DESIGN_SIZES, getQuickStart, byCategory, searchSizes,
  convertToPx, formatDimensions,
} from '../../lib/designSizes';
import { loadRecentSizes, pushRecentSize } from '../../lib/recentSizes';

const CATEGORY_ICONS = {
  'Popular Sizes':     Sparkles,
  'Social Media':      ImageIcon,
  'Marketing':         Layout,
  'Print & Documents': FileText,
  'Business':          Briefcase,
  'Events':            Calendar,
  'Education':         GraduationCap,
};

const UNITS = ['px', 'in', 'mm'];

function PresetCard({ size, onSelect, recent }) {
  const dimsLabel = recent && !size.displayLabel
    ? `${size.width} × ${size.height}`
    : formatDimensions(size);
  return (
    <button
      onClick={() => onSelect(size)}
      className="group flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-violet-400 hover:bg-violet-50/40 transition-colors text-left w-full"
    >
      <div
        className="flex-shrink-0 bg-gradient-to-br from-violet-100 to-fuchsia-100 rounded-md grid place-items-center"
        style={{
          width: 44,
          height: 44,
        }}
      >
        <div
          className="bg-white rounded-sm shadow-sm"
          style={{
            width: Math.max(8, Math.min(28, (size.width / Math.max(size.width, size.height)) * 28)),
            height: Math.max(8, Math.min(28, (size.height / Math.max(size.width, size.height)) * 28)),
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate">{size.name}</div>
        <div className="text-xs text-slate-500 truncate">{dimsLabel}</div>
      </div>
    </button>
  );
}

function QuickStartCard({ item, onSelect }) {
  if (item.isCustom) {
    return (
      <button
        onClick={() => onSelect(item)}
        className="group flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-300 hover:border-violet-500 hover:bg-violet-50/40 transition-colors aspect-[4/5] min-w-[88px]"
      >
        <Sliders className="w-5 h-5 text-slate-500 group-hover:text-violet-600 transition-colors" />
        <div className="text-xs font-semibold text-slate-700 text-center leading-tight">Custom Size</div>
      </button>
    );
  }
  return (
    <button
      onClick={() => onSelect(item)}
      className="group flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-200 hover:border-violet-400 hover:shadow-md transition-all aspect-[4/5] min-w-[88px]"
    >
      <div className="flex-1 w-full grid place-items-center">
        <div
          className="bg-gradient-to-br from-violet-200 to-fuchsia-200 rounded-md shadow-sm"
          style={{
            width: Math.max(14, Math.min(36, (item.width / Math.max(item.width, item.height)) * 36)),
            height: Math.max(14, Math.min(36, (item.height / Math.max(item.width, item.height)) * 36)),
          }}
        />
      </div>
      <div className="text-[11px] font-semibold text-slate-700 text-center leading-tight line-clamp-2">{item.name}</div>
      <div className="text-[10px] text-slate-500">{formatDimensions(item)}</div>
    </button>
  );
}

function CategoryHeader({ title }) {
  const Icon = CATEGORY_ICONS[title] || Sparkles;
  return (
    <div className="flex items-center gap-2 mb-3 mt-6">
      <Icon className="w-4 h-4 text-violet-600" />
      <h3 className="text-sm font-bold text-slate-900 tracking-tight">{title}</h3>
    </div>
  );
}

export default function CreateDesignModal({ open, onClose, onCreate }) {
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [customW, setCustomW] = useState('');
  const [customH, setCustomH] = useState('');
  const [unit, setUnit] = useState('px');
  const [customError, setCustomError] = useState('');
  const customAnchorRef = useRef(null);
  const widthInputRef = useRef(null);
  const dialogRef = useRef(null);

  // Reset state when modal opens; load recents.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCustomW(''); setCustomH(''); setUnit('px'); setCustomError('');
    let cancelled = false;
    loadRecentSizes().then((list) => { if (!cancelled) setRecent(list || []); });
    return () => { cancelled = true; };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const submit = useCallback((entry) => {
    const designType = {
      id: entry.id || 'custom',
      name: entry.name,
      category: entry.category || 'Custom Size',
    };
    const canvasSize = { width: entry.width, height: entry.height };
    pushRecentSize({ ...designType, ...canvasSize }).catch(() => {});
    onCreate({ canvasSize, designType });
  }, [onCreate]);

  const handlePresetSelect = useCallback((size) => {
    submit(size);
  }, [submit]);

  const handleQuickStart = useCallback((item) => {
    if (item.isCustom) {
      customAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => widthInputRef.current?.focus(), 250);
      return;
    }
    submit(item);
  }, [submit]);

  const handleCustomCreate = useCallback(() => {
    const wPx = convertToPx(customW, unit);
    const hPx = convertToPx(customH, unit);
    if (!wPx || !hPx) {
      setCustomError('Enter width and height greater than 0.');
      return;
    }
    if (wPx < 16 || hPx < 16) {
      setCustomError('Minimum size is 16 px on each side.');
      return;
    }
    if (wPx > 8000 || hPx > 8000) {
      setCustomError('Maximum size is 8000 px on each side.');
      return;
    }
    setCustomError('');
    submit({
      id: 'custom',
      name: `Custom ${wPx} × ${hPx}`,
      category: 'Custom Size',
      width: wPx,
      height: hPx,
    });
  }, [customW, customH, unit, submit]);

  const filtered = useMemo(() => (query.trim() ? searchSizes(query) : DESIGN_SIZES), [query]);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const cat of CATEGORIES) m.set(cat, []);
    for (const s of filtered) {
      if (!m.has(s.category)) m.set(s.category, []);
      m.get(s.category).push(s);
    }
    return m;
  }, [filtered]);

  const isSearching = query.trim().length > 0;
  const quickStart = useMemo(getQuickStart, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-design-title"
        className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh] animate-slideUp sm:animate-zoomIn"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h2 id="create-design-title" className="text-lg font-bold text-slate-900 tracking-tight">
              Create a Design
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search designs... (e.g. instagram, 1080, business card)"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-slate-50 border border-slate-200 text-sm placeholder:text-slate-400 focus:outline-none focus:border-violet-400 focus:bg-white transition-colors"
              autoFocus
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!isSearching && (
            <>
              {/* Quick Start */}
              <div className="mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Quick Start</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
                  {quickStart.map((item) => (
                    <QuickStartCard key={item.id} item={item} onSelect={handleQuickStart} />
                  ))}
                </div>
              </div>

              {/* Recently Used */}
              {recent.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-violet-600" />
                    <h3 className="text-sm font-bold text-slate-900 tracking-tight">Recently Used</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {recent.map((r, i) => (
                      <PresetCard key={`${r.width}x${r.height}-${i}`} size={r} onSelect={handlePresetSelect} recent />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Categories */}
          {CATEGORIES.map((cat) => {
            const list = grouped.get(cat) || [];
            if (list.length === 0) return null;
            return (
              <div key={cat}>
                <CategoryHeader title={cat} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map((s) => (
                    <PresetCard key={s.id} size={s} onSelect={handlePresetSelect} />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Empty search state */}
          {isSearching && filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium">No designs match "{query}"</p>
              <p className="text-xs mt-1">Try searching by name, dimensions, or category.</p>
            </div>
          )}

          {/* Custom Size */}
          {!isSearching && (
            <div ref={customAnchorRef} className="mt-8 p-4 rounded-xl border border-slate-200 bg-slate-50/60">
              <div className="flex items-center gap-2 mb-3">
                <Sliders className="w-4 h-4 text-violet-600" />
                <h3 className="text-sm font-bold text-slate-900 tracking-tight">Custom Size</h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <label className="flex-1">
                  <span className="block text-xs font-medium text-slate-600 mb-1">Width</span>
                  <input
                    ref={widthInputRef}
                    type="number"
                    min={1}
                    value={customW}
                    onChange={(e) => { setCustomW(e.target.value); setCustomError(''); }}
                    placeholder="1080"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-400 transition-colors"
                  />
                </label>
                <label className="flex-1">
                  <span className="block text-xs font-medium text-slate-600 mb-1">Height</span>
                  <input
                    type="number"
                    min={1}
                    value={customH}
                    onChange={(e) => { setCustomH(e.target.value); setCustomError(''); }}
                    placeholder="1080"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-400 transition-colors"
                  />
                </label>
                <div className="flex-shrink-0">
                  <span className="block text-xs font-medium text-slate-600 mb-1">Units</span>
                  <div className="inline-flex rounded-lg bg-white border border-slate-200 p-0.5">
                    {UNITS.map((u) => (
                      <button
                        key={u}
                        onClick={() => setUnit(u)}
                        className={`px-3 h-9 text-xs font-semibold rounded-md transition-colors ${
                          unit === u ? 'bg-violet-600 text-white' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {customError && (
                <p className="mt-2 text-xs text-red-600">{customError}</p>
              )}
              <button
                onClick={handleCustomCreate}
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              >
                Create Design <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes zoomIn { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.18s ease-out; }
        .animate-slideUp { animation: slideUp 0.24s cubic-bezier(0.16, 1, 0.3, 1); }
        @media (min-width: 640px) {
          .sm\\:animate-zoomIn { animation: zoomIn 0.18s ease-out; }
        }
      `}</style>
    </div>
  );
}
